"use client";

import Link from "next/link";

export default function HeroSection() {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-evolvia-bg dot-grid"
    >
      {/* Geometric spiral accent - inspired by logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="relative w-[800px] h-[800px] opacity-15">
          <svg viewBox="0 0 800 800" className="absolute inset-0 w-full h-full spin-slow">
            <circle cx="400" cy="400" r="320" fill="none" stroke="#C4845A" strokeWidth="0.8" strokeDasharray="8 6" />
            <circle cx="400" cy="400" r="240" fill="none" stroke="#C4845A" strokeWidth="0.6" strokeDasharray="5 8" />
            <circle cx="400" cy="400" r="160" fill="none" stroke="#C4845A" strokeWidth="0.5" strokeDasharray="3 10" />
          </svg>
          <svg viewBox="0 0 800 800" className="absolute inset-0 w-full h-full counter-spin">
            <ellipse cx="400" cy="400" rx="280" ry="70" fill="none" stroke="#C4845A" strokeWidth="0.7" strokeDasharray="6 5" transform="rotate(20, 400, 400)" />
            <ellipse cx="400" cy="400" rx="200" ry="50" fill="none" stroke="#C4845A" strokeWidth="0.5" strokeDasharray="4 7" transform="rotate(-15, 400, 400)" />
          </svg>
        </div>
      </div>
      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-evolvia-copper/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-evolvia-copper/6 rounded-full blur-[100px] pointer-events-none" />
      {/* Floating stat cards */}
      <div
        className="absolute top-32 left-8 lg:left-24 z-20 glass-card rounded-sm p-4 w-44 float-slow hidden lg:block"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1.5 h-1.5 rounded-full bg-evolvia-copper pulse-copper" />
          <span className="text-xs text-evolvia-text-muted font-600 uppercase tracking-wider">Activo</span>
        </div>
        <p className="text-2xl font-bold text-evolvia-copper">120+</p>
        <p className="text-xs text-evolvia-text-secondary">Proyectos entregados</p>
      </div>
      <div
        className="absolute top-40 right-8 lg:right-24 z-20 glass-card rounded-sm p-4 w-44 float-medium hidden lg:block"
        style={{ animationDelay: "2s" }}
      >
        <div className="flex gap-0.5 mb-2">
          {[...Array(5)]?.map((_, i) => (
            <svg key={i} className="w-3 h-3 text-evolvia-copper" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        <p className="text-2xl font-bold text-evolvia-text-primary">4.9</p>
        <p className="text-xs text-evolvia-text-secondary">Valoración clientes</p>
      </div>
      <div
        className="absolute bottom-36 left-8 lg:left-32 z-20 glass-card rounded-sm p-4 w-48 float-fast hidden lg:block"
        style={{ animationDelay: "1s" }}
      >
        <p className="text-xs text-evolvia-text-muted uppercase tracking-wider mb-1">Canarias</p>
        <p className="text-sm font-600 text-evolvia-text-primary">Las Palmas · Tenerife</p>
        <p className="text-xs text-evolvia-text-secondary mt-1">Envíos en 5–10 días</p>
      </div>
      {/* Central hero content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-evolvia-border rounded-sm px-4 py-2 mb-8">
          <div className="w-1.5 h-1.5 rounded-full bg-evolvia-copper" />
          <span className="text-xs font-600 uppercase tracking-[0.2em] text-evolvia-text-secondary">
            Diseño e Impresión 3D · Canarias
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-[clamp(3rem,8vw,7rem)] font-black leading-[0.9] tracking-[-0.02em] mb-6 text-evolvia-text-primary uppercase">
          DISEÑO QUE
          <br />
          <span className="gradient-text-copper">TRANSFORMA</span>
        </h1>

        <p className="text-lg md:text-xl text-evolvia-text-secondary font-400 leading-relaxed max-w-2xl mx-auto mb-10">
          Diseño e impresión 3D personalizada desde las Islas Canarias.
          Proyectos únicos adaptados a cada cliente, con precisión técnica y creatividad sin límites.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/homepage#portafolio" className="btn-primary">
            Ver Proyectos
          </Link>
          <Link href="/homepage#contacto" className="btn-outline">
            Solicitar Presupuesto
          </Link>
        </div>

        {/* Scroll indicator */}
        <div className="mt-16 flex flex-col items-center gap-2 opacity-40">
          <div className="w-px h-12 bg-gradient-to-b from-evolvia-copper to-transparent" />
          <span className="text-xs uppercase tracking-widest text-evolvia-text-muted">Scroll</span>
        </div>
      </div>
    </section>
  );
}